import zod, { ZodString } from "zod"
import { alphanumeric, alphanumericAndSpecialCharacters } from "../constants"
import { INVALID_EMAIL, INVALID_MESSAGE, MAXIMUM_ERROR, REQUIRED_MESSAGE } from "../constants/messages"
import { toDayJs } from "../convertor"
import dayJs, { Dayjs } from "../libs/dayjs"

type StringSchemaConfigKey = keyof ZodString
type StringSchemaConfigValueType = string | number | boolean | RegExp
type StringSchemaConfigValue = {
  value?: StringSchemaConfigValueType,
  message?: string
}
type StringSchemaConfig = Record<StringSchemaConfigKey, StringSchemaConfigValue>

type OnFlattenedErrorFound = (
  errors: Record<string, string>,
  matches: string[],
  keyFound: string,
  valueFound: string,
) => void

const transformStringToBoolean = (v: string) => {
  if (v === "true") {
    return true;
  }
  if (v === "false") {
    return false;
  }
  return v;
}

const transformStringToNull = (v: string) => {
  if (v === "null") {
    return null;
  }
  return v
}

export const getStringLengthSchemaConfigValue = (length: number, isMin = false): StringSchemaConfigValue => {
  return { value: length, message: `${isMin ? "Minimum" : "Maximum"} length is ${length}` }
}

const DefaultStringSchemaConfig = {
  max: getStringLengthSchemaConfigValue(255),
  // min: { value: 1, message: REQUIRED_MESSAGE },
  // regex: { value: /.*/g }
}

export const dayjsInstance = zod.instanceof(dayJs as unknown as typeof Dayjs, {
  message: REQUIRED_MESSAGE,
})

export const getStringSchema = (config?: Partial<StringSchemaConfig> | boolean) => {
  // to ignore the default config pass in true as parameter
  const mergedConfig = (config === true ? {} : { ...DefaultStringSchemaConfig, ...(config || {}) }) as StringSchemaConfig
  let schema = zod.string({
    required_error: REQUIRED_MESSAGE,
    // invalid_type for string mainly triggered by string value being null, hence make the message required
    invalid_type_error: REQUIRED_MESSAGE
  });
  for (const [key, { value, message }] of Object.entries(mergedConfig)) {
    const args = (value === undefined ? [] : [value, message]) as [StringSchemaConfigValueType, string] | []
    schema = (schema[key as keyof ZodString] as (value?: StringSchemaConfigValueType, message?: string) => ZodString)(...(args))
  }

  return schema
};

export const nonEmptyString = getStringSchema().min(1, REQUIRED_MESSAGE)

export const optionalString = getStringSchema().nullish()

export const requriedEmail = getStringSchema().email({ message: INVALID_EMAIL }).min(1, REQUIRED_MESSAGE)

export const optionalEmail = zod.union([zod.literal(''), getStringSchema().email({ message: INVALID_EMAIL })]) 

export const dateInput = zod
  .string({
    // this is to handle possible null value and to make the error message consistent otherwise it will show "Expected string, received null" or something like that
    message: REQUIRED_MESSAGE,
  })
  .datetime()
  .transform((v: string) => {
    return v && toDayJs(v)
  })

export const dateOutput = dayjsInstance
  .refine((v) => v.isValid(), { message: "Invalid date" })
  .transform((v: Dayjs) => {
    return dayJs(new Date(v.format("YYYY-MM-DD"))).toISOString() 
  })
export const dateTimeOutput = dayjsInstance
  .refine((v) => v.isValid(), { message: "Invalid date" })
  .transform((v: Dayjs) => {
    return v.toISOString()
  })

export const stringToBoolean = zod.string({ message: REQUIRED_MESSAGE }).transform(transformStringToBoolean)

export const stringToNull = zod.string({ message: REQUIRED_MESSAGE }).transform(transformStringToNull)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const stringToNullOrBoolean = zod.string({ message: REQUIRED_MESSAGE }).transform(transformStringToBoolean).transform(transformStringToNull as any)

export const booleanToString = zod.boolean().transform((v) => {
  if (v === true) {
    return "true";
  }
  if (v === false) {
    return "false";
  }
  return v;
})

export const OptionalNumber = (max = Infinity) => zod
  .string().or(zod.number())
  .nullish()
  .default("")
  .refine((val) => !isNaN(Number(val)), { message: INVALID_MESSAGE })
  .refine((val) => Number(val) < max, { message: MAXIMUM_ERROR(max) })

export const NonOptionalNumber = (max = Infinity) => zod.union([zod.string().min(1, REQUIRED_MESSAGE), zod.number().max(max)])
  .default("")
  .refine((val) => !isNaN(Number(val)), { message: INVALID_MESSAGE })
  .refine((val) => Number(val) < max, { message: MAXIMUM_ERROR(max) })

export function toggleStringSchema(isDraft?: boolean) {
  return isDraft ? optionalString : nonEmptyString
}
export const toggleEmailSchema = (isDraft?: boolean) => isDraft ? optionalString : requriedEmail

export function toggleSchema(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: zod.ZodSchema<any>,
  isOptianal?: boolean,
  excludingNull = false,
) {
  return isOptianal
    ? excludingNull
      ? schema.optional()
      : schema.nullish()
    : schema
}

export function flattenValues(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: Record<string, any>,
  prefix = "",
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  checkShouldStop?: (obj: Record<string, any>, key: string) => boolean
): Record<string, string> {
  const separator = "."
  return Object.keys(obj).reduce((acc, k) => {
    const pre = prefix.length ? prefix + separator : ""
    if (typeof obj[k] === "object" && obj[k] !== null && (!checkShouldStop || !checkShouldStop(obj, k))) {
      Object.assign(acc, flattenValues(obj[k], pre + k, checkShouldStop))
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (acc as Record<string, any>)[pre + k] = obj[k]
    }
    return acc
  }, {})
}

export function getErrorCount(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationErrors: Record<string, any>,
  errorKey: string,
  onErrorFound?: OnFlattenedErrorFound,
) {
  let count = 0
  const errors = flattenValues(validationErrors)
  Object.entries(errors).forEach(([key, value]) => {
    // eslint-disable-next-line no-useless-escape
    const pattern = new RegExp(`^([0-9a-z\.]*)\.(${errorKey})$`, "i")
    const matches = key.match(pattern)
    if (matches?.length) {
      if (onErrorFound) {
        onErrorFound(errors, matches, key, value)
      }
      count++
    }
  })
  return count
}

export function isSchemaFieldOptional(
  schema: zod.Schema | undefined,
  fieldPath: string,
): boolean | undefined {
  if (!schema || !fieldPath) {
    return undefined
  }

  const filteredFieldPath = removeArrayIndexFromFieldName(fieldPath) as string
  const fieldNames = filteredFieldPath.split(".")

  const fieldSchema: zod.Schema = getFieldSchema(
    schema,
    fieldNames,
    fieldNames[0],
  ) as unknown as zod.Schema

  if (!fieldSchema) {
    console.error("field schema not exist ", fieldPath)
  } else {
    // console.log("fieldSchema", fieldPath, fieldSchema, fieldSchema.isOptional())
  }

  return fieldSchema?.isOptional() || fieldSchema?.isNullable()
}

export function getFieldOptionalMap(
  fieldNameMap: Record<string, string>,
  schema: zod.ZodSchema,
): Record<string, boolean> {
  const fieldOptionalMap = {} as Record<string, boolean>
  for (const fieldPath of Object.values(fieldNameMap)) {
    fieldOptionalMap[fieldPath] = isSchemaFieldOptional(
      schema,
      fieldPath,
    ) as boolean
  }
  return fieldOptionalMap
}

export function getFieldNameMap(defaultValueMap: Record<string, string[]>): Record<string, string> {
  const fieldNameMap: Record<string, string> = {}
  for (const key in defaultValueMap) {
    fieldNameMap[key] = normalizeFieldName(defaultValueMap[key][0]) as string
  }
  return fieldNameMap;
}

export function getHiddenFields(
  allFields: string[],
  visibleFields: string[],
): string[] {
  // console.log("all and visible fields", allFields, visibleFields)
  return allFields.filter((field) => !visibleFields.includes(field))
}

export function normalizeFieldName(fieldName?: string) {
  return fieldName ? fieldName.replaceAll(/\[([0-9]+)\]\./g, ".$1.") : fieldName
}

export function removeArrayIndexFromFieldName(fieldName?: string) {
  return fieldName
    ? normalizeFieldName(fieldName)?.replaceAll(/\.[0-9]+\./g, ".")
    : fieldName
}

export function getFieldSchema(
  schema: zod.Schema,
  fieldNames: string[],
  fieldName: string,
): zod.Schema | undefined {
  // console.log("getFieldSchema ", schema, fieldNames, fieldName)
  // make sure schema is not null or undefined
  if (schema === null || schema === undefined) {
    return undefined
  }
  // check if schema is nullable or optional
  if (schema instanceof zod.ZodNullable || schema instanceof zod.ZodOptional) {
    // console.log("unwraped ", fieldName, schema, schema.unwrap())
    return getFieldSchema(schema.unwrap(), fieldNames, fieldName)
  }
  // ZodEffects are generated by refine function
  if (schema instanceof zod.ZodEffects) {
    // console.log(
    //   "instanceof zod.ZodEffects",
    //   fieldName,
    //   (schema._def as any).schema,
    // )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentSchema = (schema._def as any).schema
    return getFieldSchema(currentSchema, fieldNames, fieldName)
  }
  // check if schema is an array
  if (schema instanceof zod.ZodArray) {
    return getFieldSchema(schema.element, fieldNames, fieldName)
  }
  // check if schema is an object, only when it is object then it progress to next field
  if (schema instanceof zod.ZodObject) {
    // console.log("instance of object", fieldName, schema)
    const fieldPosition = fieldNames.indexOf(fieldName)
    const isLast = fieldPosition === fieldNames.length - 1
    const currentSchema = schema.shape[fieldName]
    if (isLast) {
      if (!currentSchema) {
        // it normally happens when the previous step is trying getting a sub schema object that is wrapped in ZodEffects (meaning the object has refine or superRefine attached)
        return schema
      }
      if (currentSchema instanceof zod.ZodEffects) {
        // console.log("instance of effects", fieldName, currentSchema)
        return getFieldSchema(currentSchema._def.schema, fieldNames, fieldName)
      }
      return currentSchema
    } else {
      return getFieldSchema(
        currentSchema,
        fieldNames,
        fieldNames[fieldPosition + 1],
      )
    }
  }

  // if(schema instanceof zod.ZodString || schema instanceof zod.ZodNumber){

  // }

  // if schema is string, number and other primitive type then we have reached bottom return
  return schema
}

export function matchArrayFieldName(fieldName: string): string[] {
  return fieldName.match(/^([a-z0-9.]+)(\.[0-9]+\.)([a-z0-9.]+)$/i) || []
}

export function unpackSchemaObject(packedSchema: zod.ZodSchema) {
  let unpackedSchema = packedSchema
  if (
    packedSchema instanceof zod.ZodOptional ||
    packedSchema instanceof zod.ZodNullable
  ) {
    unpackedSchema = packedSchema.unwrap()
  } else if (packedSchema instanceof zod.ZodArray) {
    unpackedSchema = packedSchema.element
  } else if (packedSchema instanceof zod.ZodEffects) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    unpackedSchema = (packedSchema._def as any).schema;
  }

  return unpackedSchema
}

export const getAlphanumericStringSchema = (maxChar = 255, isAlphanumeric = true) => getStringSchema({ max: getStringLengthSchemaConfigValue(maxChar) }).regex(isAlphanumeric ? alphanumeric : alphanumericAndSpecialCharacters)


export const filterSchema = zod.object({
  attribute: zod.string(),
  refinements: zod.array(
    zod.object({
      type: zod.enum(["numeric", "disjunctive"]),
      value: zod.union([zod.string(), zod.number()]),
      operator: zod.string().optional(),
    }),
  ),
})
